import type { PushProduct } from "./data"
import { TODAY, addDays, daysBetween, formatDate, shortDate } from "./dates"
import { type BasePoint, type Bucket, type Grain, dailySales, pastBuckets } from "./salesSeries"

// Stock level for the Stock graph. The past is worked backwards from today:
// each day's stock is the next day's plus what went out that day. When an
// order is coming, the graph runs on past today at the current sales, steps
// up the day the order lands, and stops three weeks after.

export interface StockPoint extends BasePoint {
  stock: number | null // past, end of the period
  expected: number | null // future, end of the period
  landing: number | null // units of the incoming order landing in this period
}

interface StockDay {
  date: string
  stock: number
  landing: number
}

// Future days grouped like pastBuckets: day by day, weeks starting tomorrow,
// or calendar months after this one (this month's point is today's stock).
function futureBuckets(days: StockDay[], grain: Grain): Bucket<StockDay>[] {
  const expected = " (expected)"
  if (grain === "day") {
    return days.map((d) => ({ tick: shortDate(d.date), title: shortDate(d.date) + expected, days: [d] }))
  }
  if (grain === "week") {
    return Array.from({ length: Math.ceil(days.length / 7) }, (_, i) => {
      const week = days.slice(i * 7, i * 7 + 7)
      const first = week[0].date
      return { tick: shortDate(first), title: `${shortDate(first)} – ${shortDate(week[week.length - 1].date)}${expected}`, days: week }
    })
  }
  const months = new Map<string, StockDay[]>()
  for (const d of days) {
    const key = d.date.slice(0, 7)
    if (key === TODAY.slice(0, 7)) continue
    months.set(key, [...(months.get(key) ?? []), d])
  }
  return [...months.entries()].map(([key, list]) => ({
    tick: formatDate(`${key}-01`, { month: "short" }),
    title: formatDate(`${key}-01`, { month: "long", year: "numeric" }) + expected,
    days: list,
  }))
}

export function stockSeries(product: PushProduct, grain: Grain): StockPoint[] {
  const sales = dailySales(product)
  let level = product.stock
  const past = [...sales].reverse().map((d) => {
    const day = { ...d, stock: level }
    level += d.sold + d.gifted
    return day
  }).reverse()

  const future: StockDay[] = []
  if (product.incoming) {
    const { qty, arrives } = product.incoming
    let expected = product.stock
    for (let i = 1; i <= daysBetween(TODAY, arrives) + 21; i++) {
      const date = addDays(TODAY, i)
      const landing = date === arrives ? qty : 0
      expected = Math.max(0, expected - product.outPerDay) + landing
      future.push({ date, stock: expected, landing })
    }
  }

  const points: StockPoint[] = pastBuckets(past, grain).map((b, i) => ({
    i,
    tick: b.tick,
    title: b.title,
    stock: b.days[b.days.length - 1].stock,
    expected: null,
    landing: null,
    actions: b.days.flatMap((d) => d.actions),
  }))
  if (future.length === 0) return points

  // The dashed line starts from today's point so the two lines join.
  points[points.length - 1].expected = points[points.length - 1].stock
  for (const b of futureBuckets(future, grain)) {
    const landing = b.days.reduce((sum, d) => sum + d.landing, 0)
    points.push({
      i: points.length,
      tick: b.tick,
      title: b.title,
      stock: null,
      expected: b.days[b.days.length - 1].stock,
      landing: landing > 0 ? landing : null,
      actions: [],
    })
  }
  return points
}
